import type {
  CapturedNetworkRequest,
  CaptureResult,
  PostHog,
} from "posthog-js";

import { redactTokens, redactTokensDeep } from "~/lib/redact-tokens";

/**
 * posthog-js `before_send`. URLs sit in many properties (`$current_url`,
 * `$referrer`, `$session_entry_url`, `$elements_chain`, heatmap keys...) and
 * in `$initial_*` person properties, so every string is redacted. A replay
 * snapshot's payload is skipped: it is large, and `redactNetworkRequestTokens`
 * covers the URLs it records. Its `$set_once` can still carry `$initial_*`.
 */
export function redactCaptureTokens(event: CaptureResult): CaptureResult {
  return {
    ...event,
    properties:
      event.event === "$snapshot"
        ? event.properties
        : redactTokensDeep(event.properties),
    ...(event.$set && { $set: redactTokensDeep(event.$set) }),
    ...(event.$set_once && { $set_once: redactTokensDeep(event.$set_once) }),
  };
}

// posthog-js stores the first visit's URL and referrer and builds the
// `$initial_*` person properties from them. /flags requests send those
// without passing through before_send, on every later page load too.
const STORED_INITIAL_INFO = ["$initial_person_info", "$initial_referrer_info"];

/**
 * Redacts the stored first-visit URL and referrer. Call it after posthog-js
 * stores them (on capture) and on load, for browsers that stored them before.
 */
export function redactStoredPersonInfo(
  client: Pick<PostHog, "get_property" | "register">,
) {
  for (const key of STORED_INITIAL_INFO) {
    const stored: unknown = client.get_property(key);
    if (!stored || typeof stored !== "object") continue;
    const redacted = redactTokensDeep(stored);
    if (JSON.stringify(redacted) !== JSON.stringify(stored)) {
      client.register({ [key]: redacted });
    }
  }
}

/**
 * `session_recording.maskCapturedNetworkRequestFn`: posthog-js passes both
 * recorded network requests and the page URLs a replay records through it.
 */
export function redactNetworkRequestTokens(
  request: CapturedNetworkRequest,
): CapturedNetworkRequest {
  if (typeof request.name !== "string") return request;
  return { ...request, name: redactTokens(request.name) };
}
